import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';

import _ from 'lodash';

import { Text, OpenCallController } from '../../components/index';

import { CallMemberCard, MessagesCard } from '../../components/Call/Card';

import Main from '../../components/VideoCall/main';

import { alertError, alertSuccess } from '../../redux/stateActions';

import { GET_CALL_SESSION_DETAIL, UPDATE_SESSION, endOpenSession } from '../../services/provider';

import socket from '../../socket';

const STATUS = Object.freeze({
    CALLING: 'CALLING',
    END: 'END',
});

const OpenCall = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    const [sessionStatus, setSessionStatus] = useState(STATUS.CALLING);

    /*--------------- Call Control Controller ---------------*/
    const roomInitRef = useRef();

    const closeWindow = () => {
        window.onbeforeunload = null;
        window.close();
    };

    /*--------------- Get Session ---------------*/
    const [sessionData, setSessionData] = useState({});
    const [participants, setParticipants] = useState([]);

    const {
        refetch: getSessionRefetch,
        data: getSessionData,
        error: getSessionError,
    } = useQuery(GET_CALL_SESSION_DETAIL, {
        variables: {
            id,
        },
        skip: !id,
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot Get Session: ${getSessionError.message}`));
        }
        if (getSessionData && !_.isEmpty(getSessionData.session)) {
            const { actualStartTime, actualEndTime, sessionEnd, cancelled, meet } =
                getSessionData.session;

            // check if video call or session end
            if (cancelled) {
                dispatch(alertError(`Session Cancelled`));
                closeWindow();
                return;
            }
            if (actualEndTime || sessionEnd) {
                dispatch(alertSuccess(`Session Ended`));
                closeWindow();
                return;
            }

            // check if start call for firstTime
            if (!actualStartTime) {
                updateSession({
                    variables: {
                        id: id,
                        actualStartTime: dayjs(),
                    },
                });
            }

            setSessionData(getSessionData.session);

            setParticipants(
                _.concat(
                    meet.staff,
                    meet.patients
                        .filter((patient) => patient.user !== null)
                        .map((patient) => patient),
                    meet.otherStaffs.filter((staff) => staff.user !== null).map((staff) => staff)
                )
            );
        }
    }, [getSessionError, getSessionData]);

    /*--------------- Update Session ---------------*/
    const [updateSession, { data: updateSessionData, error: updateSessionError }] =
        useMutation(UPDATE_SESSION);

    useEffect(() => {
        if (updateSessionError) {
            dispatch(alertError(`Cannot update Session: ${updateSessionError.message}`));
        }
        if (updateSessionData && !_.isEmpty(updateSessionData.updateSession)) {
            getSessionRefetch();
        }
    }, [updateSessionError, updateSessionData]);

    /*--------------- End Call ---------------*/
    const endCallCallback = async () => {
        try {
            socket.emit('providerEndOpenCall', {
                sessionId: id,
                meetId: sessionData.meet.id,
            });

            const res = await endOpenSession({
                sessionId: id,
            });

            if (res.status !== 200) {
                throw new Error(res);
            }

            setSessionStatus(STATUS.END);

            closeWindow();
        } catch (error) {
            dispatch(alertError(`end open session error: ${error.response?.data?.message}`));
        }
    };

    /*--------------- Member Window ---------------*/
    const [showMembers, setShowMembers] = useState(false);

    const handleCloseMemberts = () => {
        setShowMembers(false);
    };

    const handleParticipants = () => {
        setShowMessages(false);
        setShowMembers(!showMembers);
    };

    /*--------------- Messages Window ---------------*/
    const [showMessages, setShowMessages] = useState(false);
    const [messagesCount, setMessagesCount] = useState(0);

    const handleCloseMessages = () => {
        setShowMessages(false);
    };

    const handleMessages = () => {
        setShowMessages(!showMessages);
        setShowMembers(false);
    };

    useEffect(() => {
        // fire after first render. for open session
        socket.emit('joinOpenSession', { sessionId: id });

        socket.on('joinOpenSession', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
        socket.on('patientLeaveOpenCall', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
        socket.on('providerLeaveOpenCall', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
    }, []);

    return sessionStatus === STATUS.END ? (
        <div
            style={{
                height: '100vh',
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text $bold>Session Ended please close this window</Text>
        </div>
    ) : (
        <div
            style={{
                position: 'relative',
                backgroundColor: '#D2D6E9',
                width: '100vw',
                height: '100vh',
            }}
        >
            <div
                id="activity-title"
                style={{
                    padding: '15px',
                    background:
                        'linear-gradient(180deg, #494E6A 1.16%, rgba(55, 60, 89, 0.541667) 49.49%, rgba(210, 214, 233, 0) 98.84%)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1,
                }}
            >
                <Text $color="white" $ellipsis>{`Activity name - ${sessionData?.meet?.name}`}</Text>
            </div>
            <Main
                ref={roomInitRef}
                type="open"
                isProvider={true}
                faceDetection={false}
                useMicCamera={true}
            />
            <OpenCallController
                handleMic={() => roomInitRef.current.micControl()}
                handleCamera={() => roomInitRef.current.webcamControl()}
                onEndCall={() => {
                    try {
                        roomInitRef.current.providerCloseRoom(endCallCallback);
                    } catch (error) {
                        endCallCallback();
                    }
                }}
                onParticipants={handleParticipants}
                participantsCount={participants.length}
                onMessages={handleMessages}
                messagesCount={messagesCount}
            />

            <CallMemberCard
                open={showMembers}
                participants={participants}
                onClose={handleCloseMemberts}
            />
            <MessagesCard
                open={showMessages}
                participants={participants}
                onClose={handleCloseMessages}
                sender={user.staff.id}
                user={user}
                sessionId={id}
                messagesCount={messagesCount}
                setMessagesCount={setMessagesCount}
            />
        </div>
    );
};

export default OpenCall;
